import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';

import { Image, OverlayTrigger, Tooltip } from "react-bootstrap";

import HelperMetiers360 from "../../../services/HelpersMetiers360.js";
import useInfiniteLoadingLogic from "../../../hooks/useInfiniteLogic.jsx";
import TooltipListComponent from "../../widgets/tooltipList/TooltipListComponent.jsx";
import DynamicTableInfinite from "../../widgets/dynamicTableInfinite/DynamicTableInfinite.jsx";


import './VideosTableComponent.scss';
import AuthorizationChecker from "../../../services/AuthorizationChecker.js";
import PropTypes from 'prop-types';

/**
 * 
 * @param {Array} videos array of all videos to be rendered
 * @param {String} readAll  readAll slug to access the video list page
 * @param {String} readOne  readOne slug to access a video details page - replace the :uniqueId part of string with actual uniqueId
 * @param {String} create  create slug to access the video creation page - optional, need according rights
 * @param {String} update  update slug to access the video update page - replace the :uniqueId part of string with actual uniqueId  - optionnal, need according rights
 
 * @example
 *  <VideoTableComponent videos={videos} readAll={readAllSlug} create={createSlug} {...}/>
 * 
 */

const VideoTableComponent = (props) => {
    const { videos, readOne: readOneVideoSlug } = props;

    const navigate = useNavigate();
    const location = useLocation();

    const [sortedVideos, setSortedVideos] = useState([]);
    const [shouldSort, setShouldSort] = useState(false);

    useEffect(() => {
        setShouldSort(true);
        setSortedVideos([...videos]);
    }, [videos]);

    const [videosToDisplay, setVideosToDisplay] = useState([]);
    const {hasMore, fetchNext: fetchNextVideos} = useInfiniteLoadingLogic(sortedVideos, videosToDisplay, setVideosToDisplay, 40);


    const codeRome = (romes) => romes?.join(', ');



    const producersName = (producers) => producers?.length ? producers.map((producer) => producer.clientName ).map(String)?.join(', '):null;

    const getListGroupSize = (video) => {
        return video.groups ? video.groups?.length:0;
    };
    
    if (videos?.length === 0)
        return <></>;

    const thumbnailDisplay = (video) => <OverlayTrigger
            placement='right'
            overlay={
            <Tooltip>
                <Image src={video.links.thumbnail} />
            </Tooltip>
            }
        >
            <Image src={video.links.thumbnail} className="img-overlay" thumbnail />
        </OverlayTrigger>;

    const groupsDisplay = (video) => {
        if (!video.groups) {
            return <span>0</span>
        }

        const generateIdCallback = (groupUniqueId) => {
            return `group-${groupUniqueId}${video.uniqueId}`
        }

        return <TooltipListComponent inputList={video.groups} keyToDisplay='name' generateIdCallback={generateIdCallback}/>
    }

    const pedagogicModulesDisplay = (video) => {
        const pedagogicModuleList = video?.pedagogicModules || [];

        if (pedagogicModuleList?.length === 0) {
            return <span>0</span>
        }

        const generateIdCallback = (pedagogicModuleUniqueId) => {
            return `pedagogicModule-${pedagogicModuleUniqueId}`
        }

        return <TooltipListComponent
            inputList={pedagogicModuleList}
            keyToDisplay='name'
            generateIdCallback={generateIdCallback} 
        />
    }


    const storiesDisplay = (video) => {
        const storiesList = video?.stories || [];

        if (storiesList?.length === 0) {
            return <span>0</span>
        }

        const generateIdCallback = (storyUniqueId) => {
            return `story-${storyUniqueId}`
        }

        return <TooltipListComponent
            inputList={storiesList}
            keyToDisplay='name'
            generateIdCallback={generateIdCallback} />
    }





    const videoConversionStatus = (video) => {
        if (video.hasOngoingConversion) {
            return <i className='fas fa-rocket' />;
        }

        if (video.hasComingConversion) {
            return <i className="far fa-clock" />;
        }

        if (video.hasDoneConversion) {
            return <i className='fas fa-check' />;
        }

        return "-";
    }

    const sortVideoConversionStatus = (a, b) => {
        const getPriority = (video) => {
            if (video.hasOngoingConversion) return 1;
            if (video.hasComingConversion) return 2;
            if (video.hasDoneConversion) return 3;
            return 4;
        };

        return getPriority(a) - getPriority(b);
    };

    // const groupsDisplay = (video) =>

    const sortType = !sortedVideos?.length ? [] : [
        {
            value : 'thumbnail', label : 'Vignette',
            test: HelperMetiers360.isArrayContainsValue(sortedVideos, "links") && sortedVideos[0].links.thumbnail !== undefined,
            display: (video) => thumbnailDisplay(video),
            flatDisplay: (video) => video?.links?.thumbnail
        },
        {
            value: 'secondaryThumbnail', label: 'Vignette source',
            test: HelperMetiers360.isArrayContainsValue(sortedVideos, "links") && sortedVideos[0].links.secondaryThumbnail !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(!!a.links?.secondaryThumbnail, !!b.links?.secondaryThumbnail),
            display: (video) => video.links?.secondaryThumbnail ? <i className='fas fa-check' /> : <i className="fas fa-times" />,
            flatDisplay: (video) => video.links?.secondaryThumbnail ? "Oui" : "Non"
        },
        {
            value : 'name', label : 'Nom',
            test : HelperMetiers360.isArrayContainsValue(sortedVideos, "videoName"),
            method: (a,b) => HelperMetiers360.localeCompareWithNullable(a.videoName, b.videoName),
            display: (video) => video.videoName
        },
        {
            value : 'subHeading', label : 'Titre secondaire',
            test : HelperMetiers360.isArrayContainsValue(sortedVideos, "subHeading"),
            method: (a,b) => HelperMetiers360.localeCompareWithNullable(a.subHeading, b.subHeading),
            display: (video) => video.subHeading || '-'
        },
        {
            value: 'groups', label: 'Groupes',
            test: AuthorizationChecker.isAdmin() && HelperMetiers360.isArrayContainsValue(sortedVideos, "groups") ,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.groups?.length, b.groups?.length),
            display: (video) => groupsDisplay(video),
            flatDisplay: (video) => video?.groups?.length
        },
        {
            value : 'pedagogicModules', label : 'Modules',
            test : AuthorizationChecker.isAdmin() && HelperMetiers360.isArrayContainsValue(sortedVideos, "pedagogicModules"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.pedagogicModules?.length, b.pedagogicModules?.length),
            display: (video) => pedagogicModulesDisplay(video),
            flatDisplay: (video) => video.pedagogicModules?.length > 0 ? video.pedagogicModules.length : 0
        },
        {
            value: 'stories', label: 'Stories',
            test: AuthorizationChecker.isAdmin() && HelperMetiers360.isArrayContainsValue(sortedVideos, "stories"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.stories?.length, b.stories?.length),
            display: (video) => storiesDisplay(video),
            flatDisplay: (video) => video.stories.length > 0 ? video.stories.length : 0
        },
        {
            value: 'romes', label: "Codes Rome",
            test : HelperMetiers360.isArrayContainsValue(sortedVideos, "romes"),
            method: (a, b) => HelperMetiers360.sortStringArray(a.romes, b.romes),
            display: (video) => video.romes?.length ? codeRome(video.romes) : '-'
        },
        {
            value: 'pros', label: "Pros",
            test: HelperMetiers360.isArrayContainsValue(sortedVideos, "prosCount"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.prosCount, b.prosCount),
            display: (video) => video.prosCount ?? 0
        },
        {
            value: 'fileContent', label: "Fichiers",
            test: HelperMetiers360.isArrayContainsValue(sortedVideos, "files"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.files?.length, b.files?.length),
            display: (video) => {
                if (!video.files || !video.files.length) {
                    return 0
                }

                const generateIdCallback = (uniqueId) => {
                    return `file-${uniqueId}`;
                }

                return <TooltipListComponent
                    inputList={video.files}
                    keyToDisplay='localName'
                    generateIdCallback={generateIdCallback}
                />

            },
            flatDisplay: (video) => video.files.length
        },
        {
            value: 'producers', label: "Producteurs",
            test : AuthorizationChecker.isAdmin() && HelperMetiers360.isArrayContainsValue(sortedVideos, "producers"),
            method: (a, b) => HelperMetiers360.sortStringArray(a.producers?.map(x => x.clientName), b.producers?.map(x => x.clientName)),
            display: (video) => video.producers?.length ? producersName(video.producers) : '-'
        },
        {
            value: 'releaseDate', label: "Publication",
            test : HelperMetiers360.isArrayContainsValue(sortedVideos, "releaseDate"),
            method: (a, b) => HelperMetiers360.dateCompareWithNullableDescending(a.releaseDate, b.releaseDate),
            display: (video) => HelperMetiers360.getdisplayDateType(video.releaseDate, 'day')
        },
        {
            value: 'updatedAt', label: "Mise à jour",
            test : HelperMetiers360.isArrayContainsValue(sortedVideos, "updatedAt"),
            method: (a, b) => HelperMetiers360.dateCompareWithNullableDescending(a.updatedAt, b.updatedAt),
            display: (video) => HelperMetiers360.getdisplayDateType(video.updatedAt, 'day')
        },
        {
            value: 'private', label: "Privée",
            test : HelperMetiers360.isArrayContainsValue(sortedVideos, "private"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.private, b.private),
            display: (video) => video.private ? 'X' : '-'
        },
        {
            value : 'subtitles', label : "Sous-titres",
            test : HelperMetiers360.isArrayContainsValue(sortedVideos, "subtitles"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.subtitles?.length, b.subtitles?.length),
            display: (video) => video?.subtitles?.length !== 0 ? 
                video.subtitles?.map(
                    (subtitle, i) => < div key={video.uniqueId+subtitle.lang} >
                    <a href={subtitle.url} target="_blank" rel="noreferrer">
                        {subtitle.lang}
                    </a>
                    {i !== video.subtitles?.length - 1 && ", "}
                    </div>)
                : '-',
            flatDisplay: (video) => video?.subtitles?.length
        },
        {
            value : 'size', label : "Taille",
            test : AuthorizationChecker.isAdmin() && HelperMetiers360.isArrayContainsValue(sortedVideos, "size"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.size, b.size),
            display: (video) => HelperMetiers360.formatBytes(video.size, 0)
        },
        {
            value : 'duration', label : "durée",
            test : AuthorizationChecker.isAdmin() && HelperMetiers360.isArrayContainsValue(sortedVideos, "videoLength"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.videoLength, b.videoLength),
            display: (video) => video.videoLength !== null ? HelperMetiers360.formatDuration(video.videoLength, 's'):'-'
        },
        {
            value: 'position', label: "Position",
            test : HelperMetiers360.isArrayContainsValue(sortedVideos, "position"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.position, b.position),
            display: (video) => video.position
        },
        {
            value: 'directAccessCode', label: "Code d'accès",
            test : HelperMetiers360.isArrayContainsValue(sortedVideos, "directAccessCode"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.directAccessCode, b.directAccessCode),
            display: (video) => video.isDirectlyAccessible && video.directAccessCode
        },
        {
            value: 'hasOriginalMedia', label: 'Fichier original',
            test: HelperMetiers360.isArrayContainsValue(sortedVideos, "hasOriginalMedia"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.hasOriginalMedia, b.hasOriginalMedia),
            display: (video) => video.hasOriginalMedia ? <i className='fas fa-check' /> : <i className="fas fa-times" />,
            flatDisplay: (video) => video.hasOriginalMedia ? "Oui" : "Non"
        },
        {
            value: 'countVideoMedias', label: 'Video médias',
            test: HelperMetiers360.isArrayContainsValue(sortedVideos, "countVideoMedias"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.countVideoMedias, b.countVideoMedias),
            display: (video) => video.countVideoMedias ? video.countVideoMedias : 0,
            flatDisplay: (video) => video.countVideoMedias ? video.countVideoMedias : 0,
        },
        {
            value: 'conversion', label: "Conversion",
            test: HelperMetiers360.isArrayContainsValue(sortedVideos, "hasDoneConversion"),
            method: (a, b) => sortVideoConversionStatus(a, b),
            display: (video) => videoConversionStatus(video),
            flatDisplay: (video) => video.hasDoneConversion ? "Oui" : "Non"
        }, {
            value: 'headset', label: <i className='nav-icon icon-VR-M360' />,
            test: HelperMetiers360.isArrayContainsValue(sortedVideos, "headsetsCount"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.headsetsCount, b.headsetsCount),
            display: (video) => video.headsetsCount ? video.headsetsCount : 0
        },
        {
            value: 'headsetViews', label: <><i className='fas fa-play' /> <i className='nav-icon icon-VR-M360' /></>,
            test: HelperMetiers360.isArrayContainsValue(sortedVideos, "headsetViews"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.headsetViews, b.headsetViews),
            display: (video) => video.headsetViews ? video.headsetViews : 0
        },
        {
            value: 'webapp2Views', label: <><i className='fas fa-play' /> <i className='nav-icon icon-mobile' /></>,
            test: HelperMetiers360.isArrayContainsValue(sortedVideos, "webapp2Views"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.webapp2Views, b.webapp2Views),
            display: (video) => video.webapp2Views ? video.webapp2Views : 0
        }
    ];

    const handleClick = (uniqueId) => {
        if (readOneVideoSlug) navigate(readOneVideoSlug.replace(':uniqueId', uniqueId), { state: { olderSearchParams: location.search } });
    }

    return videosToDisplay.length > 0
        && <DynamicTableInfinite 
            contentTable = {videosToDisplay}
            contentSort = {sortType}
        valueInitSort="releaseDate"
            index = 'uniqueId'
            handleClick={handleClick}
            fetchData={fetchNextVideos}
            hasMore={hasMore}
            setSortedContent={setSortedVideos}
            sortedContent={sortedVideos}
            sortState={[shouldSort, setShouldSort]}
        withParams={true}
            tableName="videosTable"
            filename="experiences_immersives" />;
}

VideoTableComponent.propTypes = {
    videos : PropTypes.array.isRequired,
    readOne: PropTypes.string,
};

export default VideoTableComponent;